import { useEffect } from 'react';

// Replicates the solar/lunar math from DynamicSunLogo for favicon rendering.
// No cloud patterns — clean icon for the browser tab.

const LAT = -34.6037, LON = -58.3816, TZ = -3;
const toRad = d => d * Math.PI / 180;
const toDeg = r => r * 180 / Math.PI;

const L = { horizonY: 62, arcTop: 14, arcLeft: 12, arcRight: 88, sunR: 7, glowR: 16, moonR: 5.5 };

function getDayOfYear(d) { return Math.floor((d - new Date(d.getFullYear(), 0, 0)) / 86400000); }

function getSolarData(date) {
    const N = getDayOfYear(date);
    const decl = -toDeg(Math.asin(0.39779 * Math.cos(toRad(0.98565 * (N + 10) + 1.914 * Math.sin(toRad(0.98565 * (N - 2)))))));
    const B = toRad(360 / 365.24 * (N - 81));
    const eot = 9.87 * Math.sin(2 * B) - 7.53 * Math.cos(B) - 1.5 * Math.sin(B);
    const tc = eot + 4 * (LON - 15 * TZ);
    const hours = date.getHours() + date.getMinutes() / 60 + date.getSeconds() / 3600;
    const ha = 15 * (hours + tc / 60 - 12);
    const altitude = toDeg(Math.asin(Math.sin(toRad(LAT)) * Math.sin(toRad(decl)) + Math.cos(toRad(LAT)) * Math.cos(toRad(decl)) * Math.cos(toRad(ha))));
    const cosH = -Math.tan(toRad(LAT)) * Math.tan(toRad(decl));
    const H0 = cosH < -1 ? 180 : cosH > 1 ? 0 : toDeg(Math.acos(cosH));
    const solarNoon = 12 - tc / 60;
    const sunrise = solarNoon - H0 / 15, sunset = solarNoon + H0 / 15;
    const maxAlt = toDeg(Math.asin(Math.sin(toRad(LAT)) * Math.sin(toRad(decl)) + Math.cos(toRad(LAT)) * Math.cos(toRad(decl))));
    const dayFrac = (sunset - sunrise) > 0 ? (hours - sunrise) / (sunset - sunrise) : 0.5;
    return { altitude, maxAlt, dayFrac, solarNoon };
}

function getMoonData(date) {
    const REF = new Date(Date.UTC(2000, 0, 6, 18, 14, 0));
    const phase = (((date.getTime() - REF.getTime()) / 86400000 / 29.53058770576) % 1 + 1) % 1;
    const illumination = (1 - Math.cos(2 * Math.PI * phase)) / 2;
    const solar = getSolarData(date);
    const transit = (solar.solarNoon + phase * 24) % 24;
    const moonrise = ((transit - 6.2) % 24 + 24) % 24;
    const hours = date.getHours() + date.getMinutes() / 60;
    let hs = hours - moonrise; if (hs < -12) hs += 24; if (hs > 24) hs -= 24;
    const frac = hs / 12.4;
    const maxAlt = 35 + 30 * Math.abs(Math.cos(Math.PI * phase));
    const alt = (frac >= 0 && frac <= 1) ? maxAlt * Math.sin(Math.PI * frac) : -10;
    return { phase, illumination, alt, maxAlt, frac };
}

function lerpHue(h0, h1, t) {
    if (Math.abs(h1 - h0) > 180) { if (h1 > h0) h0 += 360; else h1 += 360; }
    return ((h0 + (h1 - h0) * t) % 360 + 360) % 360;
}

function glowColor(alt, maxAlt, dayFrac) {
    const stops = [[0,.005,100,45],[.08,15,100,50],[.2,30,100,55],[.35,45,100,58],[.5,48,100,62],[.65,45,100,58],[.8,340,85,55],[.92,300,80,50],[1,270,75,42]];
    const df = Math.max(0, Math.min(1, dayFrac));
    let i = 0; while (i < stops.length - 2 && stops[i + 1][0] < df) i++;
    const [f0,h0,s0,l0] = stops[i], [f1,h1,s1,l1] = stops[i+1];
    const t = f1 > f0 ? (df - f0) / (f1 - f0) : 0;
    const h = lerpHue(h0, h1, t), s = s0 + (s1 - s0) * t, l = l0 + (l1 - l0) * t;
    let alpha;
    if (alt <= -6) alpha = 0;
    else if (alt <= 0) alpha = 0.55 * Math.max(0, (alt + 6) / 6);
    else { const r = Math.min(alt / Math.max(maxAlt, 1), 1); const e = 1 - 2 * Math.abs(df - 0.5); alpha = 0.25 + 0.45 * r + 0.2 * e * (1 - r); }
    return `hsla(${Math.round(h)},${Math.round(s)}%,${Math.round(l)}%,${alpha.toFixed(3)})`;
}

function buildFaviconSVG() {
    const now = new Date();
    const solar = getSolarData(now);
    const moon = getMoonData(now);
    const { altitude, maxAlt, dayFrac } = solar;

    // Sun position
    const sx = L.arcRight - dayFrac * (L.arcRight - L.arcLeft);
    const sy = L.horizonY - (altitude / Math.max(maxAlt, 1)) * (L.horizonY - L.arcTop);
    const x = Math.max(L.arcLeft - 10, Math.min(L.arcRight + 10, sx));
    const y = sy;
    const glow = glowColor(altitude, maxAlt, dayFrac);
    const altThresh = -(L.sunR / (L.horizonY - L.arcTop)) * maxAlt;
    const showSun = altitude > altThresh;
    const showTwilight = !showSun && altitude > -6;
    const edgeness = 1 - 2 * Math.abs(Math.max(0, Math.min(1, dayFrac)) - 0.5);
    const dynamicGlowR = L.glowR + 6 * edgeness * (altitude > 0 && altitude < 20 ? 1 : 0);

    // Reflections opacity
    let refOp;
    if (altitude > 5) refOp = 0.85;
    else if (altitude > 0) refOp = 0.4 + 0.45 * (altitude / 5);
    else if (altitude > -6) refOp = 0.15 + 0.25 * ((altitude + 6) / 6);
    else refOp = 0.12;

    // Moon
    const mx = L.arcRight - Math.max(0, Math.min(1, moon.frac)) * (L.arcRight - L.arcLeft);
    const my = L.horizonY - (moon.alt / Math.max(moon.maxAlt, 1)) * (L.horizonY - L.arcTop);
    const moonAltThresh = -(L.moonR / (L.horizonY - L.arcTop)) * Math.max(moon.maxAlt, 1);
    const showMoon = moon.alt > moonAltThresh && moon.frac >= -0.05 && moon.frac <= 1.05;
    const moonOp = altitude > 15 ? 0 : altitude > 5 ? (1 - (altitude - 5) / 10) : 1;
    const k = Math.cos(2 * Math.PI * moon.phase);
    const flipX = moon.phase < 0.5 ? -1 : 1;
    const mr = L.moonR;
    const moonGlowA = (0.08 + 0.15 * moon.illumination).toFixed(3);

    let svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="64" height="64">`;
    svg += `<defs><clipPath id="fc"><rect x="0" y="0" width="100" height="${L.horizonY}"/></clipPath></defs>`;

    if (showSun) {
        svg += `<g clip-path="url(#fc)">`;
        svg += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${dynamicGlowR}" fill="${glow}"/>`;
        svg += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${L.sunR}" fill="black"/>`;
        svg += `</g>`;
    } else if (showTwilight) {
        svg += `<g clip-path="url(#fc)">`;
        svg += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${L.glowR * 1.5}" fill="${glow}"/>`;
        svg += `</g>`;
    }

    if (showMoon && moonOp > 0.01) {
        const mcx = Math.max(L.arcLeft - 10, Math.min(L.arcRight + 10, mx)).toFixed(1);
        const mcy = my.toFixed(1);
        svg += `<g clip-path="url(#fc)" opacity="${moonOp.toFixed(2)}">`;
        svg += `<circle cx="${mcx}" cy="${mcy}" r="${mr * 2.2}" fill="hsla(220,30%,75%,${moonGlowA})"/>`;
        svg += `<circle cx="${mcx}" cy="${mcy}" r="${mr}" fill="#e8e8e8" stroke="black" stroke-width="1.2"/>`;
        svg += `<path d="M ${mcx} ${(mcy - mr).toFixed(1)} A ${mr} ${mr} 0 0 ${flipX > 0 ? 1 : 0} ${mcx} ${(parseFloat(mcy) + mr).toFixed(1)} A ${(Math.abs(k) * mr).toFixed(2)} ${mr} 0 0 ${k * flipX > 0 ? 0 : 1} ${mcx} ${(mcy - mr).toFixed(1)} Z" fill="#1a1a1a" opacity="0.85"/>`;
        svg += `</g>`;
    }

    // Horizon
    svg += `<line x1="5" y1="${L.horizonY}" x2="95" y2="${L.horizonY}" stroke="black" stroke-width="2.5" stroke-linecap="round"/>`;

    // Reflections
    svg += `<line x1="30" y1="${L.horizonY + 9}" x2="70" y2="${L.horizonY + 9}" stroke="black" stroke-width="2.2" opacity="${refOp.toFixed(2)}" stroke-linecap="round"/>`;
    svg += `<line x1="35" y1="${L.horizonY + 16}" x2="65" y2="${L.horizonY + 16}" stroke="black" stroke-width="2" opacity="${(refOp * 0.7).toFixed(2)}" stroke-linecap="round"/>`;
    svg += `<line x1="40" y1="${L.horizonY + 23}" x2="60" y2="${L.horizonY + 23}" stroke="black" stroke-width="1.7" opacity="${(refOp * 0.45).toFixed(2)}" stroke-linecap="round"/>`;

    svg += `</svg>`;
    return svg;
}

function setFavicon(svgString) {
    const encoded = 'data:image/svg+xml,' + encodeURIComponent(svgString);
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
    }
    link.type = 'image/svg+xml';
    link.href = encoded;
}

const DynamicFavicon = () => {
    useEffect(() => {
        setFavicon(buildFaviconSVG());
        const timer = setInterval(() => setFavicon(buildFaviconSVG()), 300000); // 5 min
        return () => clearInterval(timer);
    }, []);
    return null; // Renders nothing — just manages the favicon
};

export default DynamicFavicon;
